import React, { useState } from 'react';
import { ampelColor } from './KbsLayer';
import { trainGroupLabels, HIGH_DELAY_THRESHOLD_MIN } from './trainPosition';
import JourneyList from './trainJourneys';

const GROUP_ORDER = ['fern', 'nah', 'sbahn', 'sonstige', 'total'];

const fmtPct = (v) => (v === null || v === undefined ? '–' : `${v.toFixed(1)} %`);

// Percentage-point difference against the previous operating day, as in the report
const ppDiff = (today, prev) => {
  if (today === null || prev === null || today === undefined || prev === undefined) return null;
  const diff = today - prev;
  return {
    text: `${diff >= 0 ? '+' : ''}${diff.toFixed(1)} pp`,
    good: diff >= 0,
  };
};

const priorityColor = (p) => (p === 'HOCH' ? '#f85149' : p === 'MITTEL' ? '#f0b429' : '#8b949e');

/**
 * Statistics for a single station — the same block the daily operations mail contains:
 * volume and punctuality per traffic group, median and average delay, high delays,
 * track changes, the seven-day trend, the worst trains and the most frequent messages.
 */
const StationPanel = ({
  station, stats, loading,
  board, boardLoading, boardType, onBoardTypeChange, onJourneySelect,
  onClose,
}) => {
  const [tab, setTab] = useState('board');
  if (!station) return null;

  const trend = stats?.trend || [];
  const maxTrendCount = Math.max(1, ...trend.map(t => t.count));
  const showBoard = tab === 'board';

  return (
    <aside className="kbs-drillin station-panel">
      <div className="kbs-drillin-header">
        <button className="kbs-drillin-close" onClick={onClose} title="Schließen">
          <i className="fa fa-arrow-left"></i>
        </button>
        <div className="kbs-drillin-headtext">
          <div className="kbs-drillin-title">
            <i className="fa fa-building-o"></i> {station.name}
          </div>
          <div className="kbs-drillin-date">
            {showBoard
              ? `${boardType === 'arrival' ? 'Ankünfte' : 'Abfahrten'} · Stand der Karte`
              : `Betriebstag ${stats?.opDay || ''} · 04:00–04:00`}
          </div>
        </div>
      </div>

      <div className="tp-tabs">
        <button
          className={`tp-tab ${showBoard && boardType === 'departure' ? 'active' : ''}`}
          onClick={() => { setTab('board'); onBoardTypeChange('departure'); }}
        >
          <i className="fa fa-sign-out"></i> Abfahrten
        </button>
        <button
          className={`tp-tab ${showBoard && boardType === 'arrival' ? 'active' : ''}`}
          onClick={() => { setTab('board'); onBoardTypeChange('arrival'); }}
        >
          <i className="fa fa-sign-in"></i> Ankünfte
        </button>
        <button
          className={`tp-tab ${tab === 'stats' ? 'active' : ''}`}
          onClick={() => setTab('stats')}
        >
          <i className="fa fa-bar-chart"></i> Statistik
        </button>
      </div>

      {showBoard && (
        <JourneyList
          key={`${station.uuid}-${boardType}`}
          stationUUID={station.uuid}
          journeys={board || []}
          loading={boardLoading}
          timetableType={boardType}
          compact
          onJourneySelect={onJourneySelect}
        />
      )}

      {tab === 'stats' && loading && (
        <div className="kbs-drillin-loading"><i className="fa fa-spinner fa-spin"></i> lädt…</div>
      )}

      {tab === 'stats' && stats && (
        <>
          <div className="kbs-drillin-section">
            <h5>Verkehr und Pünktlichkeit</h5>
            <table className="station-table">
              <thead>
                <tr>
                  <th>Verkehr</th>
                  <th className="num">Bew.</th>
                  <th className="num">pünktlich</th>
                  <th className="num">Median</th>
                  <th className="num">Ø</th>
                  <th className="num" title={`Hochverspätung ab ${HIGH_DELAY_THRESHOLD_MIN} min`}>≥60</th>
                  <th className="num" title="Gleisänderungen">Gleis</th>
                </tr>
              </thead>
              <tbody>
                {GROUP_ORDER.map(group => {
                  const m = stats.groups[group];
                  if (!m || m.count === 0) return null;
                  const prev = stats.prevGroups?.[group];
                  const diff = ppDiff(m.on_time_pct, prev?.on_time_pct);
                  return (
                    <tr key={group} className={group === 'total' ? 'total-row' : ''}>
                      <td>{group === 'total' ? 'Gesamt' : trainGroupLabels[group]}</td>
                      <td className="num">{m.count}</td>
                      <td className="num">
                        <span style={{ color: ampelColor(m.on_time_pct) }}>{fmtPct(m.on_time_pct)}</span>
                        {diff && (
                          <span className={`station-pp ${diff.good ? 'up' : 'down'}`}>{diff.text}</span>
                        )}
                      </td>
                      <td className="num">{m.delay_median} min</td>
                      <td className="num">{m.delay_avg.toFixed(1)}</td>
                      <td className="num">{m.high_delay || '–'}</td>
                      <td className="num">{m.track_changes || '–'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {stats.cancelled > 0 && (
              <p className="station-cancelled">
                <i className="fa fa-ban"></i> {stats.cancelled} ausgefallene Züge
              </p>
            )}
          </div>

          <div className="kbs-drillin-section">
            <h5>Pünktlichkeit letzte 7 Betriebstage (Fernverkehr)</h5>
            <div className="station-trend">
              {trend.map(t => (
                <div
                  key={t.date}
                  className="station-trend-col"
                  title={t.count > 0
                    ? `${t.date}: ${fmtPct(t.on_time_pct)} bei ${t.count} Zügen`
                    : `${t.date}: keine Daten`}
                >
                  <div
                    className="station-trend-bar"
                    style={{
                      height: `${t.on_time_pct !== null ? t.on_time_pct : 0}%`,
                      background: ampelColor(t.on_time_pct),
                      opacity: 0.35 + 0.65 * (t.count / maxTrendCount),
                    }}
                  />
                  <span className="station-trend-label">{t.date.slice(8)}</span>
                </div>
              ))}
            </div>
            <p className="station-hint">
              Nur Fernverkehr — der Verkehrsmix bleibt dort über Tage stabil genug zum Vergleichen.
            </p>
          </div>

          <div className="kbs-drillin-section">
            <h5>Verspätetste Züge (Verspätung am Ziel)</h5>
            {stats.topDelayed.length === 0 && <p className="kbs-empty">Keine Verspätungen</p>}
            <ol className="kbs-top-list">
              {stats.topDelayed.map((t, i) => (
                <li key={`${t.trainName}-${i}`}>
                  <span className="kbs-top-name">{t.trainName}</span>
                  <span className="kbs-top-meta">
                    {t.sched} → {t.act} · {t.terminus}
                  </span>
                  <span className="kbs-top-delay">+{t.delay} min</span>
                </li>
              ))}
            </ol>
          </div>

          <div className="kbs-drillin-section">
            <h5>Häufigste Meldungen</h5>
            {stats.reasons.length === 0 && (
              <p className="kbs-empty">Keine Echtzeit-Meldungen im Berichtszeitraum.</p>
            )}
            <ul className="station-reasons">
              {stats.reasons.map(r => (
                <li key={r.messageText}>
                  <span className="station-reason-text">{r.messageText}</span>
                  <span className="station-reason-meta">
                    {r.affected_trains} Züge
                    <b style={{ color: priorityColor(r.priority) }}>[{r.priority}]</b>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </aside>
  );
};

export default StationPanel;
